import React, { useContext } from "react";
import {
  hasStyledProps,
  hasBorderProps,
  isStyledExtension,
} from "@sproutsocial/seeds-react-system-props";
import Icon from "@sproutsocial/seeds-react-icon";
import { useChildContext, SubComponentContext } from "./utils";
import type {
  TypeCardLink,
  TypeSharedCardSystemProps,
  TypeStyledSelectedIcon,
} from "./CardTypes";
import {
  StyledCardContent,
  StyledCardHeader,
  StyledCardFooter,
  StyledCardAffordance,
  StyledCardLink,
} from "./styles";

interface TypeSharedSubComponentProps extends TypeSharedCardSystemProps {
  children?: React.ReactNode;
}

// Utility for merging class names. Mirrored inline from Avatar —
// @sproutsocial/seeds-react-utilities does NOT export a `cn` helper.
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

// Route to the styled-components path when a styled-system prop, a BORDER-mixin
// prop, or a styled() extension is present; otherwise render the Tailwind path.
function needsStyled(props: Record<string, any>): boolean {
  return (
    hasStyledProps(props) || hasBorderProps(props) || isStyledExtension(props)
  );
}

export const CardContent = (props: TypeSharedSubComponentProps) => {
  // TODO: It could be cool to possibly adjust the context to include an array of names of child components.
  // Then, if CardHeader or CardFooter aren't used with CardContent throw an error.
  useChildContext();
  const { children, className, ...rest } = props;

  if (needsStyled(props)) {
    return (
      <StyledCardContent {...rest} className={className}>
        {children}
      </StyledCardContent>
    );
  }

  return (
    <div
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
      className={cn("seeds-card-content", className)}
    >
      {children}
    </div>
  );
};

export const CardHeader = (props: TypeSharedSubComponentProps) => {
  useChildContext();
  const { children, className, ...rest } = props;

  if (needsStyled(props)) {
    return (
      <StyledCardHeader {...rest} className={className}>
        {children}
      </StyledCardHeader>
    );
  }

  return (
    <div
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
      className={cn("seeds-card-header", className)}
    >
      {children}
    </div>
  );
};

export const CardFooter = (props: TypeSharedSubComponentProps) => {
  useChildContext();
  const { children, className, ...rest } = props;

  if (needsStyled(props)) {
    return (
      <StyledCardFooter {...rest} className={className}>
        {children}
      </StyledCardFooter>
    );
  }

  return (
    <div
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
      className={cn("seeds-card-footer", className)}
    >
      {children}
    </div>
  );
};

interface TypeSelectedIconProps {
  $selected?: TypeStyledSelectedIcon["$selected"];
}

// Internal, never receives consumer system props — rendered directly on the
// Tailwind path. The overlay positioning, background, and opacity transition
// live in card.css (.seeds-card-selected-icon-wrapper / .seeds-card-selected-icon).
export const SelectedIcon = ({ $selected }: TypeSelectedIconProps) => {
  return (
    <div className="seeds-card-selected-icon-wrapper">
      <Icon
        aria-hidden
        color="icon.base"
        name="circle-check-solid"
        className={cn("seeds-card-selected-icon", {
          "seeds-card-selected-icon-visible": !!$selected,
        })}
      />
    </div>
  );
};

export const CardAffordance = ({ className, ...rest }: Record<string, any>) => {
  if (needsStyled(rest) || isStyledExtension({ className })) {
    return (
      <StyledCardAffordance
        {...rest}
        className={className}
        size="mini"
        name="arrow-right-solid"
        // TODO: probably need to make this available to the top level for external links https://sprout.atlassian.net/browse/DS-2223
        aria-hidden
      />
    );
  }

  return (
    <Icon
      {...rest}
      className={cn("seeds-card-affordance", className)}
      size="mini"
      name="arrow-right-solid"
      aria-hidden
    />
  );
};

export const CardLink = ({
  affordance,
  children,
  external = false,
  color,
  className,
  ...rest
}: React.PropsWithChildren<TypeCardLink>) => {
  const { href, linkRef } = useContext(SubComponentContext);

  // Because we are hijacking Card click event to directly click this link, we need to stop propagation to avoid a double click event.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
  };

  const sharedProps = {
    target: external ? "_blank" : undefined,
    rel: external ? "noreferrer" : undefined,
    href,
    onClick: handleClick,
  };

  // color/typography are styled-system props → route those to the styled path.
  if (
    color !== undefined ||
    hasStyledProps(rest) ||
    hasBorderProps(rest) ||
    isStyledExtension({ ...rest, className })
  ) {
    return (
      <StyledCardLink
        {...rest}
        {...sharedProps}
        ref={linkRef}
        className={className}
        // TODO: fix this type since `color` should be valid here. TS can't resolve the correct type.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        color={color}
      >
        <>
          {children}
          {affordance ? <CardAffordance ml={300} /> : null}
        </>
      </StyledCardLink>
    );
  }

  return (
    <a
      {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      {...sharedProps}
      ref={linkRef}
      className={cn("seeds-card-link", className)}
    >
      <>
        {children}
        {affordance ? <CardAffordance className="ml-300" /> : null}
      </>
    </a>
  );
};
